import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { ArrowLeft, MapPin, Clock, Phone, Globe, Star, Navigation, Store, Flag, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { motion } from "framer-motion";
import gustoAvatar from "@/assets/gusto-avatar.png";
import gustoAvatarWhite from "@/assets/gusto-avatar-white.png";

const DAYS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];

const StoreDetailPage = () => {
  const { storeId } = useParams();
  const { user, token } = useAuth();
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showClaimDialog, setShowClaimDialog] = useState(false);
  const [claimName, setClaimName] = useState("");
  const [claimEmail, setClaimEmail] = useState("");
  const [claimMessage, setClaimMessage] = useState("");
  const [claiming, setClaiming] = useState(false);
  const [claimed, setClaimed] = useState(false);

  useEffect(() => {
    const fetchStore = async () => {
      try {
        const res = await axios.get(`${API}/stores/${storeId}`);
        setStore(res.data);
      } catch {
        toast.error("Commerce introuvable");
      } finally {
        setLoading(false);
      }
    };
    fetchStore();
  }, [storeId]);

  const handleClaim = async () => {
    if (!claimName.trim() || !claimEmail.trim()) {
      toast.error("Veuillez remplir les champs obligatoires");
      return;
    }
    setClaiming(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/stores/${storeId}/claim`, {
        name: claimName.trim(),
        email: claimEmail.trim(),
        message: claimMessage.trim(),
      }, { headers });
      setClaimed(true);
      setShowClaimDialog(false);
      toast.success("Demande envoyée ! Nous vous contacterons sous 48h.");
    } catch {
      toast.error("Erreur lors de l'envoi");
    } finally {
      setClaiming(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!store) {
    return (
      <div className="px-4 py-12 text-center">
        <Store className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
        <p className="text-muted-foreground">Commerce introuvable</p>
        <Link to="/stores"><Button className="mt-4 rounded-full">Voir l'annuaire</Button></Link>
      </div>
    );
  }

  const isClaimed = store.claimed;

  return (
    <div className="pb-8" data-testid="store-detail-page">
      {/* Cover */}
      <div className="relative aspect-[21/9] md:aspect-[3/1] bg-gray-100 overflow-hidden">
        {store.cover_image ? (
          <img src={store.cover_image} alt={store.name} className={`w-full h-full object-cover ${!isClaimed ? "blur-[6px] scale-105" : ""}`} />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
            <img src={gustoAvatarWhite} alt="" className="w-24 h-24 opacity-20" />
          </div>
        )}
        {/* Salmon overlay */}
        <div className="absolute inset-0 bg-primary/25" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
        {!isClaimed && (
          <div className="absolute inset-0 flex items-center justify-center">
            <img src={gustoAvatarWhite} alt="" className="w-16 h-16 opacity-40 drop-shadow-lg" />
          </div>
        )}
        <Link
          to="/stores"
          className="absolute top-4 left-4 w-10 h-10 rounded-full bg-white/90 flex items-center justify-center shadow-md hover:bg-white"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        {!isClaimed && (
          <Badge className="absolute top-4 right-4 bg-amber-100 text-amber-700">
            <Flag className="w-3 h-3 mr-1" /> Fiche en attente
          </Badge>
        )}
      </div>

      <div className="px-4 -mt-8 relative z-10 max-w-3xl mx-auto">
        {/* Info Card */}
        <Card className="shadow-lg">
          <CardContent className="p-5">
            <div className="flex items-start gap-3">
              <div className="w-14 h-14 rounded-xl bg-white border border-gray-100 flex items-center justify-center flex-shrink-0 overflow-hidden shadow-sm -mt-8">
                {store.logo_url ? (
                  <img src={store.logo_url} alt="" className="w-full h-full object-contain p-1" />
                ) : (
                  <img src={gustoAvatarWhite} alt="" className="w-8 h-8 opacity-20" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="font-heading text-xl font-bold text-charcoal">{store.name}</h1>
                <Badge variant="outline" className="text-xs mt-1">{store.category_label}</Badge>
              </div>
            </div>

            {store.description && (
              <p className="text-sm text-muted-foreground mt-3">{store.description}</p>
            )}

            <div className="mt-4 space-y-2">
              <p className="text-sm flex items-center gap-2">
                <MapPin className="w-4 h-4 text-primary flex-shrink-0" />
                <span className="text-muted-foreground">{store.address}, {store.postal_code} {store.city}</span>
              </p>
              {store.phone && (
                <p className="text-sm flex items-center gap-2">
                  <Phone className="w-4 h-4 text-primary flex-shrink-0" />
                  <a href={`tel:${store.phone}`} className="text-primary hover:underline">{store.phone}</a>
                </p>
              )}
              {store.website && (
                <p className="text-sm flex items-center gap-2">
                  <Globe className="w-4 h-4 text-primary flex-shrink-0" />
                  <a href={store.website} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline truncate">{store.website}</a>
                </p>
              )}
            </div>

            {/* Claim banner */}
            {!isClaimed && !claimed && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-5 p-4 rounded-xl bg-amber-50 border border-amber-200"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Flag className="w-4 h-4 text-amber-600" />
                  <p className="font-heading font-semibold text-sm text-amber-800">Vous êtes le propriétaire ?</p>
                </div>
                <p className="text-xs text-amber-700 mb-3">
                  Revendiquez cette fiche pour gérer votre profil, ajouter vos produits et recevoir des commandes.
                </p>
                <Button
                  size="sm"
                  onClick={() => setShowClaimDialog(true)}
                  className="rounded-full bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="claim-btn"
                >
                  Revendiquer cette page
                </Button>
              </motion.div>
            )}
            {claimed && (
              <div className="mt-5 p-4 rounded-xl bg-green-50 border border-green-200 flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                <p className="text-sm text-green-800">Votre demande est en cours de traitement. Nous vous contacterons sous 48h.</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Opening Hours */}
        {store.opening_hours && (
          <Card className="mt-4">
            <CardContent className="p-5">
              <h2 className="font-heading font-semibold text-charcoal flex items-center gap-2 mb-3">
                <Clock className="w-4 h-4 text-primary" /> Horaires d'ouverture
              </h2>
              <div className="space-y-1.5">
                {DAYS.map((day) => {
                  const hours = store.opening_hours[day.toLowerCase()];
                  const isToday = new Date().toLocaleDateString("fr-FR", { weekday: "long" }).toLowerCase() === day.toLowerCase();
                  return (
                    <div key={day} className={`flex items-center justify-between text-sm py-1 px-2 rounded ${isToday ? "bg-primary/5 font-medium" : ""}`}>
                      <span className={isToday ? "text-primary" : "text-charcoal"}>{day}</span>
                      <span className={`text-sm ${hours === "Fermé" ? "text-red-500" : "text-muted-foreground"}`}>
                        {hours || "—"}
                      </span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Claim Dialog */}
      <Dialog open={showClaimDialog} onOpenChange={setShowClaimDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Revendiquer cette fiche</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">Remplissez ce formulaire pour revendiquer <strong>{store.name}</strong>. Notre équipe vérifiera votre demande.</p>
            <div>
              <label className="text-xs font-medium text-charcoal mb-1 block">Nom complet *</label>
              <Input value={claimName} onChange={(e) => setClaimName(e.target.value)} placeholder="Jean Dupont" />
            </div>
            <div>
              <label className="text-xs font-medium text-charcoal mb-1 block">Email professionnel *</label>
              <Input type="email" value={claimEmail} onChange={(e) => setClaimEmail(e.target.value)} placeholder="contact@commerce.fr" />
            </div>
            <div>
              <label className="text-xs font-medium text-charcoal mb-1 block">Message (optionnel)</label>
              <Textarea value={claimMessage} onChange={(e) => setClaimMessage(e.target.value)} rows={3} placeholder="Informations complémentaires..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowClaimDialog(false)} className="rounded-full">Annuler</Button>
            <Button onClick={handleClaim} disabled={claiming} className="rounded-full gap-2">
              {claiming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Flag className="w-4 h-4" />}
              Envoyer la demande
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StoreDetailPage;
